// components/SongQueueModal.js - Host song request queue with approval
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMusic, FiX, FiCheck, FiTrash2, FiMessageCircle, FiClock } from 'react-icons/fi';
import supabase from '../services/supabase';
import '../styles/SongQueueModal.scss';

const SongQueueModal = ({ onClose, eventId, financialYear }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'rejected', 'all'

  const loadRequests = useCallback(async () => {
    try {
      let query = supabase
        .from('song_requests')
        .select(`
          *,
          users:user_id (
            id,
            name,
            profile_image_url
          )
        `)
        .eq('event_id', eventId)
        .eq('financial_year', financialYear)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;
      if (error) throw error;

      setRequests(data || []);
    } catch (error) {
      console.error('Error loading song requests:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, financialYear, filter]);

  useEffect(() => {
    loadRequests();

    // Subscribe to real-time updates
    const subscription = supabase
      .channel(`song_requests_${eventId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'song_requests',
        filter: `event_id=eq.${eventId}`
      }, () => {
        loadRequests();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [eventId, financialYear, loadRequests]);

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from('song_requests')
        .update({
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(requests.map(req =>
        req.id === requestId ? { ...req, status: 'approved' } : req
      ));
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from('song_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(requests.map(req =>
        req.id === requestId ? { ...req, status: 'rejected' } : req
      ));
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (requestId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this request?')) return;

    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from('song_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(requests.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Failed to delete request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const filteredRequests = requests;
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="song-queue-modal"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <FiMusic size={24} />
            <div>
              <h2>Song Request Queue</h2>
              <p>Manage song requests from guests</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending {pendingCount > 0 && `(${pendingCount})`}
          </button>
          <button
            className={`filter-tab ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved {approvedCount > 0 && `(${approvedCount})`}
          </button>
          <button
            className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected {rejectedCount > 0 && `(${rejectedCount})`}
          </button>
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({requests.length})
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="empty-state">
              <FiMusic size={48} />
              <p>No {filter !== 'all' ? filter : ''} requests yet</p>
            </div>
          ) : (
            <div className="requests-list">
              <AnimatePresence>
                {filteredRequests.map((request) => (
                  <motion.div
                    key={request.id}
                    className={`request-card ${request.status}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    layout
                  >
                    <div className="request-content">
                      <img
                        src={request.thumbnail}
                        alt={request.title}
                        className="song-thumbnail"
                      />
                      <div className="song-details">
                        <h4>{request.title}</h4>
                        <div className="song-meta">
                          <span className="channel">
                            <FiMusic size={12} />
                            {request.channel_title}
                          </span>
                          <span className="time">
                            <FiClock size={12} />
                            {formatTimeAgo(request.created_at)}
                          </span>
                        </div>
                        <div className="requester-info">
                          <img
                            src={request.users?.profile_image_url || '/default-avatar.png'}
                            alt={request.users?.name}
                            className="requester-avatar"
                          />
                          <span className="requester-name">
                            {request.users?.name || 'Unknown User'}
                          </span>
                        </div>
                        {request.message && (
                          <div className="request-message">
                            <FiMessageCircle size={14} />
                            <p>{request.message}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="request-actions">
                      {request.status === 'pending' && (
                        <>
                          <button
                            className="action-btn approve-btn"
                            onClick={() => handleApprove(request.id)}
                            disabled={processingId === request.id}
                          >
                            <FiCheck size={18} />
                            Approve
                          </button>
                          <button
                            className="action-btn reject-btn"
                            onClick={() => handleReject(request.id)}
                            disabled={processingId === request.id}
                          >
                            <FiX size={18} />
                            Reject
                          </button>
                        </>
                      )}
                      {request.status !== 'pending' && (
                        <div className={`status-badge ${request.status}`}>
                          {request.status === 'approved' && <FiCheck size={14} />}
                          {request.status === 'rejected' && <FiX size={14} />}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </div>
                      )}
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(request.id)}
                        disabled={processingId === request.id}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SongQueueModal;
